import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Loader2, Pin, MessageCircle, Heart, Send, Bell, BellOff, Trash2, ListTodo,
  BarChart3, Paperclip, X, Plus, CalendarPlus, Image as ImageIcon, FileText, Download,
  Smile, Mic, Square,
} from 'lucide-react';
import emojiData from '@emoji-mart/data';
import EmojiPicker from '@emoji-mart/react';
import { apiClient } from '@/lib/api';
import { getInitials, type FeedPost, type Workgroup, type FeedPoll, type FeedAttachment } from './types';

interface EmojiSelectEvent { native?: string }
interface SttResponse { text?: string }

const STT_MAX_MS = 120_000;

function pickAudioMime(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
  }
  return 'audio/webm';
}

function mmss(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

interface Props {
  workgroup: Workgroup;
  currentUserId: string | null;
  canManage: boolean;
}

interface FeedComment {
  id: string;
  author_id: string;
  author_first_name?: string | null;
  author_last_name?: string | null;
  content_text: string;
  created_at: string;
}

interface PostExtras {
  is_followed?: boolean;
}

interface PollDraft {
  question: string;
  options: string[];
  is_multiple_choice: boolean;
  is_anonymous: boolean;
  closes_at: string;
}

interface AppointmentDraft {
  postId: string;
  title: string;
  date: string;
  start: string;
  end: string;
}

const EMPTY_POLL: PollDraft = {
  question: '',
  options: ['', ''],
  is_multiple_choice: false,
  is_anonymous: false,
  closes_at: '',
};

function formatBytes(n: number | null | undefined): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageMime(m: string | null | undefined): boolean {
  return !!m && m.startsWith('image/');
}

export default function ChatTab({ workgroup, currentUserId, canManage }: Props) {
  const [posts, setPosts] = useState<Array<FeedPost & PostExtras>>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState<Record<string, FeedComment[]>>({});
  const [commentDraft, setCommentDraft] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<File[]>([]);
  const [pollDraft, setPollDraft] = useState<PollDraft | null>(null);
  const [appointmentDraft, setAppointmentDraft] = useState<AppointmentDraft | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [sttBusy, setSttBusy] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const emojiWrapRef = useRef<HTMLDivElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTsRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const mimeRef = useRef<string>('audio/webm');

  useEffect(() => {
    if (!showEmoji) return;
    const onDown = (e: MouseEvent) => {
      if (emojiWrapRef.current && !emojiWrapRef.current.contains(e.target as Node)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showEmoji]);

  useEffect(() => {
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      mediaStreamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  const insertAtCaret = useCallback((text: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      setDraft(prev => prev + text);
      return;
    }
    const start = ta.selectionStart ?? draft.length;
    const end = ta.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + text + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  }, [draft]);

  const onEmojiSelect = useCallback((e: EmojiSelectEvent) => {
    if (e?.native) insertAtCaret(e.native);
    setShowEmoji(false);
  }, [insertAtCaret]);

  const stopMicStream = () => {
    mediaStreamRef.current?.getTracks().forEach(t => t.stop());
    mediaStreamRef.current = null;
  };

  const transcribeBlob = async (blob: Blob) => {
    setSttBusy(true);
    setComposerError(null);
    try {
      const ext = mimeRef.current.includes('mp4') ? 'mp4' : 'webm';
      const fd = new FormData();
      fd.append('audio', blob, `dictation.${ext}`);
      fd.append('language', 'it');
      const res = await apiClient.post<SttResponse>('/personal-assistant/workspace/stt', fd);
      const text = (res?.data?.text ?? '').trim();
      if (text) {
        const sep = draft.length > 0 && !/\s$/.test(draft) ? ' ' : '';
        setDraft(prev => prev + sep + text);
        requestAnimationFrame(() => textareaRef.current?.focus());
      } else {
        setComposerError('Nessun testo riconosciuto');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore trascrizione';
      setComposerError(msg);
    } finally {
      setSttBusy(false);
    }
  };

  const startDictation = async () => {
    if (recording || sttBusy) return;
    setComposerError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mime = pickAudioMime();
      mimeRef.current = mime;
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        stopMicStream();
        void transcribeBlob(blob);
      };
      rec.start();
      mediaRecorderRef.current = rec;
      startTsRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      tickRef.current = window.setInterval(() => {
        const e = Date.now() - startTsRef.current;
        setElapsed(e);
        if (e >= STT_MAX_MS) stopDictation();
      }, 200);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Microfono non disponibile';
      setComposerError(msg);
      stopMicStream();
    }
  };

  const stopDictation = () => {
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch { /* noop */ }
    }
    setRecording(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<Array<FeedPost & PostExtras>>(
        `/messenger/feed/posts?target_type=workgroup&target_id=${workgroup.id}&limit=30`,
      );
      const list: Array<FeedPost & PostExtras> = Array.isArray(res.data) ? [...res.data] : [];
      list.sort((a, b) => Number(b.is_pinned) - Number(a.is_pinned) || (b.created_at > a.created_at ? 1 : -1));
      setPosts(list);
    } finally {
      setLoading(false);
    }
  }, [workgroup.id]);

  useEffect(() => { load(); }, [load]);

  function resetComposer() {
    setDraft('');
    setAttachments([]);
    setPollDraft(null);
  }

  async function handlePost() {
    const hasText = draft.trim().length > 0;
    const hasPoll = pollDraft !== null;
    const hasFiles = attachments.length > 0;
    if (!hasText && !hasPoll && !hasFiles) return;

    if (hasPoll) {
      const cleanOpts = pollDraft!.options.map(o => o.trim()).filter(Boolean);
      if (!pollDraft!.question.trim() || cleanOpts.length < 2) {
        alert('Domanda e almeno 2 opzioni sono obbligatorie');
        return;
      }
    }

    setPosting(true);
    try {
      const fd = new FormData();
      fd.append('post_type', hasPoll ? 'poll' : 'message');
      fd.append('target_type', 'workgroup');
      fd.append('target_id', workgroup.id);
      if (hasText) {
        fd.append('content_html', `<p>${draft.replace(/\n/g, '<br>')}</p>`);
      }
      if (hasPoll) {
        const cleanOpts = pollDraft!.options.map(o => o.trim()).filter(Boolean);
        fd.append('poll', JSON.stringify({
          question: pollDraft!.question.trim(),
          options: cleanOpts,
          is_multiple_choice: pollDraft!.is_multiple_choice,
          is_anonymous: pollDraft!.is_anonymous,
          closes_at: pollDraft!.closes_at ? new Date(pollDraft!.closes_at).toISOString() : undefined,
        }));
      }
      for (const f of attachments) fd.append('files', f);

      await apiClient.post('/messenger/feed/posts', fd);
      resetComposer();
      load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore';
      alert(`Errore creazione post: ${msg}`);
    } finally {
      setPosting(false);
    }
  }

  async function handleReact(post: FeedPost) {
    const liked = post.my_reaction === '❤';
    try {
      if (liked) {
        await apiClient.delete(`/messenger/feed/posts/${post.id}/reactions/${encodeURIComponent('❤')}`);
      } else {
        await apiClient.post(`/messenger/feed/posts/${post.id}/reactions`, { emoji: '❤' });
      }
      load();
    } catch { /* noop */ }
  }

  async function togglePin(post: FeedPost) {
    if (post.is_pinned) {
      await apiClient.delete(`/messenger/feed/posts/${post.id}/pin`);
    } else {
      await apiClient.post(`/messenger/feed/posts/${post.id}/pin`, {});
    }
    load();
  }

  async function toggleFollow(post: FeedPost & PostExtras) {
    if (post.is_followed) {
      await apiClient.delete(`/messenger/feed/posts/${post.id}/follow`);
    } else {
      await apiClient.post(`/messenger/feed/posts/${post.id}/follow`, {});
    }
    load();
  }

  async function deletePost(postId: string) {
    if (!confirm('Eliminare il post?')) return;
    await apiClient.delete(`/messenger/feed/posts/${postId}`);
    load();
  }

  async function loadComments(postId: string) {
    if (openComments[postId]) {
      const next = { ...openComments };
      delete next[postId];
      setOpenComments(next);
      return;
    }
    const res = await apiClient.get<FeedComment[]>(`/messenger/feed/posts/${postId}/comments`);
    setOpenComments({ ...openComments, [postId]: Array.isArray(res.data) ? res.data : [] });
  }

  async function postComment(postId: string) {
    const text = commentDraft[postId]?.trim();
    if (!text) return;
    await apiClient.post(`/messenger/feed/posts/${postId}/comments`, {
      content_html: `<p>${text.replace(/\n/g, '<br>')}</p>`,
    });
    setCommentDraft({ ...commentDraft, [postId]: '' });
    const res = await apiClient.get<FeedComment[]>(`/messenger/feed/posts/${postId}/comments`);
    setOpenComments({ ...openComments, [postId]: Array.isArray(res.data) ? res.data : [] });
    load();
  }

  async function createTaskFromPost(postId: string) {
    await apiClient.post(`/messenger/feed/posts/${postId}/create-task`, {});
    alert('Task creata');
  }

  async function votePoll(poll: FeedPoll, optionId: string) {
    const alreadyVoted = poll.user_votes.includes(optionId);
    try {
      if (alreadyVoted) {
        await apiClient.delete(`/messenger/feed/polls/${poll.id}/vote?option_id=${encodeURIComponent(optionId)}`);
      } else {
        await apiClient.post(`/messenger/feed/polls/${poll.id}/vote`, { option_id: optionId });
      }
      load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore';
      alert(`Errore voto: ${msg}`);
    }
  }

  async function revokeAllVotes(poll: FeedPoll) {
    try {
      await apiClient.delete(`/messenger/feed/polls/${poll.id}/vote`);
      load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore';
      alert(`Errore revoca voto: ${msg}`);
    }
  }

  function openAppointmentModal(post: FeedPost) {
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    setAppointmentDraft({
      postId: post.id,
      title: post.title || (post.content_text || '').slice(0, 80) || 'Appuntamento',
      date,
      start: '09:00',
      end: '10:00',
    });
  }

  async function submitAppointment() {
    if (!appointmentDraft) return;
    const { postId, title, date, start, end } = appointmentDraft;
    if (!date || !start || !end) { alert('Compila data e orari'); return; }
    const startIso = new Date(`${date}T${start}:00`).toISOString();
    const endIso = new Date(`${date}T${end}:00`).toISOString();
    try {
      await apiClient.post(`/messenger/feed/posts/${postId}/create-appointment`, {
        direct_create: true,
        title,
        start_time: startIso,
        end_time: endIso,
      });
      setAppointmentDraft(null);
      alert('Appuntamento creato');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore';
      alert(`Errore creazione appuntamento: ${msg}`);
    }
  }

  function onFilesPicked(ev: React.ChangeEvent<HTMLInputElement>) {
    const files = ev.target.files ? Array.from(ev.target.files) : [];
    if (files.length === 0) return;
    setAttachments(prev => [...prev, ...files].slice(0, 10));
    ev.target.value = '';
  }

  function renderAttachment(att: FeedAttachment) {
    const href = `/api/messenger/feed/attachments/${att.id}/download`;
    if (isImageMime(att.mime_type)) {
      return (
        <a key={att.id} href={href} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-border bg-muted">
          <img src={href} alt={att.filename} className="max-h-64 object-contain w-full" />
        </a>
      );
    }
    return (
      <a key={att.id} href={href} target="_blank" rel="noreferrer"
         className="flex items-center gap-2 px-3 py-2 bg-muted border border-border rounded-lg text-sm hover:bg-muted/70">
        <FileText size={16} className="text-muted-foreground flex-shrink-0" />
        <span className="flex-1 truncate text-foreground">{att.filename}</span>
        <span className="text-xs text-muted-foreground">{formatBytes(att.file_size)}</span>
        <Download size={14} className="text-muted-foreground" />
      </a>
    );
  }

  function renderPoll(poll: FeedPoll) {
    const closed = poll.closes_at ? new Date(poll.closes_at) < new Date() : false;
    const userHasVoted = poll.user_votes.length > 0;
    const total = Math.max(poll.total_votes, 1);
    return (
      <div className="mt-3 p-3 bg-muted/40 border border-border rounded-lg">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="font-medium text-foreground text-sm">{poll.question}</div>
          {closed && <span className="text-xs text-amber-400">Chiuso</span>}
        </div>
        <div className="space-y-1.5">
          {poll.options.map(opt => {
            const pct = poll.total_votes > 0 ? Math.round((opt.vote_count / total) * 100) : 0;
            const voted = poll.user_votes.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                disabled={closed}
                onClick={() => votePoll(poll, opt.id)}
                className={`relative w-full text-left rounded-md border ${voted ? 'border-blue-500/60' : 'border-border'} overflow-hidden disabled:opacity-60 hover:border-blue-400/60 transition-colors`}
              >
                <div className={`absolute inset-y-0 left-0 ${voted ? 'bg-blue-500/20' : 'bg-muted'}`} style={{ width: `${pct}%` }} />
                <div className="relative flex items-center justify-between px-3 py-1.5 text-sm">
                  <span className="text-foreground">{opt.option_text}</span>
                  <span className="text-xs text-muted-foreground">{opt.vote_count} · {pct}%</span>
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
          <span>{poll.total_votes} voti {poll.is_anonymous ? '· anonimo' : ''} {poll.is_multiple_choice ? '· scelta multipla' : ''}</span>
          {userHasVoted && !closed && (
            <button onClick={() => revokeAllVotes(poll)} className="text-blue-400 hover:text-blue-300">Revoca voto</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border bg-card/40 space-y-2">
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={pollDraft ? 'Aggiungi una descrizione (opzionale)...' : 'Scrivi un aggiornamento per il gruppo...'}
            rows={2}
            className="flex-1 bg-muted border border-border rounded-lg px-3 py-2 text-foreground text-sm resize-none"
          />
          <button
            onClick={handlePost}
            disabled={posting || (!draft.trim() && !pollDraft && attachments.length === 0)}
            className="px-4 bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-lg text-primary-foreground"
          >
            {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => setPollDraft(pollDraft ? null : { ...EMPTY_POLL })}
            className={`flex items-center gap-1.5 px-2 py-1 rounded border ${pollDraft ? 'border-blue-500/60 text-blue-400' : 'border-border text-muted-foreground hover:text-foreground'}`}
          >
            <BarChart3 size={14} /> Sondaggio
          </button>
          <label className="flex items-center gap-1.5 px-2 py-1 rounded border border-border text-muted-foreground hover:text-foreground cursor-pointer">
            <Paperclip size={14} /> Allegati
            <input type="file" multiple className="hidden" onChange={onFilesPicked} />
          </label>
          <div ref={emojiWrapRef} className="relative">
            <button
              type="button"
              onClick={() => setShowEmoji(v => !v)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded border ${showEmoji ? 'border-blue-500/60 text-blue-400' : 'border-border text-muted-foreground hover:text-foreground'}`}
              aria-label="Emoji"
              title="Emoji"
            >
              <Smile size={14} /> Emoji
            </button>
            {showEmoji && (
              <div className="absolute bottom-full left-0 z-50 mb-2">
                <EmojiPicker
                  data={emojiData}
                  onEmojiSelect={onEmojiSelect}
                  theme="auto"
                  locale="it"
                  previewPosition="none"
                  skinTonePosition="none"
                />
              </div>
            )}
          </div>
          {!recording ? (
            <button
              type="button"
              onClick={startDictation}
              disabled={sttBusy}
              className="flex items-center gap-1.5 px-2 py-1 rounded border border-border text-muted-foreground hover:text-foreground disabled:opacity-50"
              aria-label="Detta messaggio"
              title="Detta messaggio"
            >
              {sttBusy ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
              {sttBusy ? 'Invio…' : 'Detta'}
            </button>
          ) : (
            <button
              type="button"
              onClick={stopDictation}
              className="flex items-center gap-1.5 px-2 py-1 rounded border border-red-500/60 text-red-400"
              aria-label="Stop registrazione"
              title="Stop registrazione"
            >
              <Square size={14} />
              <span className="font-mono">{mmss(elapsed)} / 02:00</span>
            </button>
          )}
          {composerError && (
            <span className="text-red-400 ml-1 truncate max-w-[200px]" title={composerError}>{composerError}</span>
          )}
          {attachments.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              {attachments.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-foreground/80">
                  {f.type.startsWith('image/') ? <ImageIcon size={12} /> : <FileText size={12} />}
                  <span className="max-w-[120px] truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                    className="hover:text-red-400"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {pollDraft && (
          <div className="p-3 bg-muted/40 border border-border rounded-lg space-y-2">
            <input
              value={pollDraft.question}
              onChange={(e) => setPollDraft({ ...pollDraft, question: e.target.value })}
              placeholder="Domanda del sondaggio"
              className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm text-foreground"
            />
            <div className="space-y-1.5">
              {pollDraft.options.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={opt}
                    onChange={(e) => {
                      const next = [...pollDraft.options];
                      next[i] = e.target.value;
                      setPollDraft({ ...pollDraft, options: next });
                    }}
                    placeholder={`Opzione ${i + 1}`}
                    className="flex-1 bg-background border border-border rounded px-2 py-1 text-sm text-foreground"
                  />
                  {pollDraft.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setPollDraft({
                        ...pollDraft,
                        options: pollDraft.options.filter((_, idx) => idx !== i),
                      })}
                      className="px-2 text-muted-foreground hover:text-red-400"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              {pollDraft.options.length < 20 && (
                <button
                  type="button"
                  onClick={() => setPollDraft({ ...pollDraft, options: [...pollDraft.options, ''] })}
                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                >
                  <Plus size={12} /> Aggiungi opzione
                </button>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={pollDraft.is_multiple_choice}
                  onChange={(e) => setPollDraft({ ...pollDraft, is_multiple_choice: e.target.checked })}
                /> Scelta multipla
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={pollDraft.is_anonymous}
                  onChange={(e) => setPollDraft({ ...pollDraft, is_anonymous: e.target.checked })}
                /> Anonimo
              </label>
              <label className="flex items-center gap-1.5">
                Scadenza
                <input
                  type="datetime-local"
                  value={pollDraft.closes_at}
                  onChange={(e) => setPollDraft({ ...pollDraft, closes_at: e.target.value })}
                  className="bg-background border border-border rounded px-1.5 py-0.5 text-foreground"
                />
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && <div className="text-center text-muted-foreground py-8"><Loader2 className="animate-spin inline" /></div>}
        {!loading && posts.length === 0 && (
          <div className="text-center text-muted-foreground/80 py-8 text-sm">Nessun post nel feed. Inizia tu!</div>
        )}
        {posts.map(post => {
          const isAuthor = post.author_id === currentUserId;
          const comments = openComments[post.id];
          const liked = post.my_reaction === '❤';
          return (
            <article key={post.id} className="bg-card border border-border rounded-xl p-4">
              {post.is_pinned && (
                <div className="flex items-center gap-1.5 text-xs text-amber-400 mb-2">
                  <Pin size={12} /> Post pinnato
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-foreground text-xs flex-shrink-0 overflow-hidden">
                  {post.author_avatar_url ? (
                    <img src={post.author_avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    getInitials(`${post.author_first_name ?? ''} ${post.author_last_name ?? ''}`)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-foreground">
                      {post.author_first_name} {post.author_last_name}
                    </span>
                    <span className="text-xs text-muted-foreground/80">{new Date(post.created_at).toLocaleString('it-IT')}</span>
                  </div>
                  {post.title && <h3 className="font-semibold text-foreground mt-1">{post.title}</h3>}
                  {post.content_html && (
                    <div
                      className="prose prose-invert prose-sm max-w-none mt-2 text-foreground"
                      dangerouslySetInnerHTML={{ __html: post.content_html }}
                    />
                  )}

                  {post.poll && renderPoll(post.poll)}

                  {post.attachments && post.attachments.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {post.attachments.map(renderAttachment)}
                    </div>
                  )}

                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground flex-wrap">
                    <button
                      onClick={() => handleReact(post)}
                      className={`flex items-center gap-1 hover:text-pink-400 ${liked ? 'text-pink-400' : ''}`}
                    >
                      <Heart size={14} fill={liked ? 'currentColor' : 'none'} />
                      {post.reactions_count ?? 0}
                    </button>
                    <button onClick={() => loadComments(post.id)} className="flex items-center gap-1 hover:text-foreground">
                      <MessageCircle size={14} /> {post.comments_count ?? 0}
                    </button>
                    <button onClick={() => toggleFollow(post)} className="flex items-center gap-1 hover:text-foreground">
                      {post.is_followed ? <Bell size={14} /> : <BellOff size={14} />}
                    </button>
                    {canManage && (
                      <button onClick={() => togglePin(post)} className={`flex items-center gap-1 hover:text-amber-400 ${post.is_pinned ? 'text-amber-400' : ''}`}>
                        <Pin size={14} />
                      </button>
                    )}
                    <button onClick={() => createTaskFromPost(post.id)} className="flex items-center gap-1 hover:text-blue-400" title="Crea task da post">
                      <ListTodo size={14} />
                    </button>
                    <button onClick={() => openAppointmentModal(post)} className="flex items-center gap-1 hover:text-emerald-400" title="Crea appuntamento da post">
                      <CalendarPlus size={14} />
                    </button>
                    {(isAuthor || canManage) && (
                      <button onClick={() => deletePost(post.id)} className="flex items-center gap-1 hover:text-red-400 ml-auto">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {comments && (
                    <div className="mt-3 pl-3 border-l-2 border-border space-y-2">
                      {comments.map(c => (
                        <div key={c.id} className="text-sm">
                          <span className="font-medium text-foreground">{c.author_first_name} {c.author_last_name}</span>
                          <span className="text-xs text-muted-foreground/80 ml-2">{new Date(c.created_at).toLocaleString('it-IT')}</span>
                          <p className="text-foreground/80">{c.content_text}</p>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          value={commentDraft[post.id] ?? ''}
                          onChange={(e) => setCommentDraft({ ...commentDraft, [post.id]: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && postComment(post.id)}
                          placeholder="Commenta..."
                          className="flex-1 bg-muted border border-border rounded px-2 py-1 text-xs text-foreground"
                        />
                        <button onClick={() => postComment(post.id)} className="text-blue-400 hover:text-blue-300 text-xs">
                          Invia
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {appointmentDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setAppointmentDraft(null)}>
          <div className="bg-card border border-border rounded-xl p-5 w-full max-w-md space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Crea appuntamento</h3>
              <button onClick={() => setAppointmentDraft(null)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
            </div>
            <div className="space-y-2 text-sm">
              <label className="block">
                <span className="text-xs text-muted-foreground">Titolo</span>
                <input
                  value={appointmentDraft.title}
                  onChange={e => setAppointmentDraft({ ...appointmentDraft, title: e.target.value })}
                  className="w-full bg-muted border border-border rounded px-2 py-1.5 text-foreground"
                />
              </label>
              <label className="block">
                <span className="text-xs text-muted-foreground">Data</span>
                <input
                  type="date"
                  value={appointmentDraft.date}
                  onChange={e => setAppointmentDraft({ ...appointmentDraft, date: e.target.value })}
                  className="w-full bg-muted border border-border rounded px-2 py-1.5 text-foreground"
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs text-muted-foreground">Inizio</span>
                  <input
                    type="time"
                    value={appointmentDraft.start}
                    onChange={e => setAppointmentDraft({ ...appointmentDraft, start: e.target.value })}
                    className="w-full bg-muted border border-border rounded px-2 py-1.5 text-foreground"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-muted-foreground">Fine</span>
                  <input
                    type="time"
                    value={appointmentDraft.end}
                    onChange={e => setAppointmentDraft({ ...appointmentDraft, end: e.target.value })}
                    className="w-full bg-muted border border-border rounded px-2 py-1.5 text-foreground"
                  />
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setAppointmentDraft(null)} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">Annulla</button>
              <button onClick={submitAppointment} className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90">Crea</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
